import React from 'react';
import { Home, Eye, MessageSquare, Clock, TrendingUp } from 'lucide-react';

interface DashboardKPIsProps {
  activeCount: number;
  pendingCount: number;
  totalViews: number;
  totalInquiries: number;
  totalListings: number;
}

export default function DashboardKPIs({
  activeCount,
  pendingCount,
  totalViews,
  totalInquiries,
  totalListings,
}: DashboardKPIsProps) {
  const cards = [
    {
      id: 'kpi-active',
      label: 'Active Listings',
      value: activeCount,
      subtext: `of ${totalListings} total listings`,
      icon: Home,
      color: 'bg-green-50 text-green-700',
      iconBg: 'bg-green-100',
      trend: null,
      alert: false,
    },
    {
      id: 'kpi-pending',
      label: 'Pending Approval',
      value: pendingCount,
      subtext: pendingCount > 0 ? 'Awaiting admin review' : 'All listings reviewed',
      icon: Clock,
      color: pendingCount > 0 ? 'bg-amber-50 text-amber-700' : 'bg-muted text-muted-foreground',
      iconBg: pendingCount > 0 ? 'bg-amber-100' : 'bg-muted',
      trend: null,
      alert: pendingCount > 0,
    },
    {
      id: 'kpi-views',
      label: 'Total Property Views',
      value: totalViews.toLocaleString('en-IN'),
      subtext: 'Across all active listings',
      icon: Eye,
      color: 'bg-blue-50 text-blue-700',
      iconBg: 'bg-blue-100',
      trend: '+18% vs last month',
      alert: false,
    },
    {
      id: 'kpi-inquiries',
      label: 'Total Inquiries',
      value: totalInquiries,
      subtext: 'Contact requests received',
      icon: MessageSquare,
      color: 'bg-primary/5 text-primary',
      iconBg: 'bg-primary/10',
      trend: `${Math.round((totalInquiries / Math.max(totalViews, 1)) * 100)}% contact rate`,
      alert: false,
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 2xl:gap-5">
      {cards.map((card) => (
        <div
          key={card.id}
          className={`rounded-2xl border p-5 card-shadow ${
            card.alert
              ? 'bg-amber-50 border-amber-200' :'bg-card border-border'
          }`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                {card.label}
              </div>
              <div className="text-3xl font-extrabold text-foreground tabular-nums mb-1">
                {card.value}
              </div>
              <div className="text-xs text-muted-foreground">{card.subtext}</div>
              {card.trend && (
                <div className="flex items-center gap-1 mt-2 text-xs font-semibold text-green-600">
                  <TrendingUp size={11} />
                  {card.trend}
                </div>
              )}
              {card.alert && (
                <div className="text-xs font-semibold text-amber-600 mt-2">
                  ⚠ Review required
                </div>
              )}
            </div>
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${card.iconBg}`}>
              <card.icon size={20} className={card.color.split(' ')[1]} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}